import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { Button, Input, Tag } from "../../../../design-system";
import {
  CanonicalHost,
  CustomDomainStatus,
  DnsInstruction,
  connectLandingDomain,
  checkLandingDomainStatus,
  disconnectLandingDomain,
} from "../../../../database/landingPage/customDomainApi";

type Props = {
  communityId?: string;
  // Snapshot from landingPage/info._customDomain, if any.
  initialDomain?: { host?: string; status?: string } | null;
};

const Panel = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
`;
const Title = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;
const Intro = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin: 0;
  max-width: 720px;
  line-height: 1.6;
`;
const Row = styled.div`
  display: flex;
  gap: var(--space-3);
  align-items: flex-end;
  flex-wrap: wrap;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 260px;
  flex: 1;
`;
const Label = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;
const Table = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  overflow: hidden;
`;
const HeadRow = styled.div`
  display: grid;
  grid-template-columns: 100px 120px 1fr;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  background: var(--color-surface-subtle);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;
const DnsRow = styled.div`
  display: grid;
  grid-template-columns: 100px 120px 1fr;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--color-border);
  font-family: var(--font-mono, monospace);
  font-size: var(--font-size-xs);
  color: var(--color-text);
  overflow-wrap: anywhere;
`;
const AdvancedToggle = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-accent);
`;
const Choice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text);
  line-height: 1.5;
  cursor: pointer;
`;
const ChoiceHint = styled.span`
  color: var(--color-text-secondary);
`;
const Provider = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  flex-wrap: wrap;
`;
const ProviderLink = styled.a`
  color: var(--color-accent);
`;
const Warn = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-warning-text, #9a6700);
`;
const StatusText: Record<string, string> = {
  pending_dns: "Waiting for DNS",
  verifying: "Verifying & issuing certificate",
  active: "Live",
  error: "Needs attention",
};

const CustomDomainPanel = ({ communityId, initialDomain }: Props) => {
  const [domain, setDomain] = useState("");
  // The canonical twin. www is the default, and the only value an already
  // connected domain can have been created with.
  const [canonical, setCanonical] = useState<CanonicalHost>("www");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [status, setStatus] = useState<CustomDomainStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasDomain = !!(status?.primaryHost || initialDomain?.host);
  const currentHost = status?.primaryHost || initialDomain?.host || "";
  const currentStatus = status?.status || (initialDomain?.status as any) || "";

  const refresh = useCallback(async () => {
    if (!communityId || !initialDomain?.host) return;
    setBusy(true);
    try {
      setStatus(await checkLandingDomainStatus(communityId));
    } catch (e: any) {
      setError(e?.message || "Could not check status");
    } finally {
      setBusy(false);
    }
  }, [communityId, initialDomain?.host]);

  useEffect(() => {
    if (initialDomain?.host) refresh();
  }, [initialDomain?.host, refresh]);

  const onConnect = async () => {
    if (!communityId || !domain.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setStatus(await connectLandingDomain(communityId, domain.trim(), canonical));
      setDomain("");
    } catch (e: any) {
      setError(e?.message || "Could not connect domain");
    } finally {
      setBusy(false);
    }
  };

  const onDisconnect = async () => {
    if (!communityId) return;
    setBusy(true);
    setError(null);
    try {
      await disconnectLandingDomain(communityId);
      setStatus(null);
    } catch (e: any) {
      setError(e?.message || "Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  const records: DnsInstruction[] = status?.records || [];
  // Only present when discovery found the coach's DNS provider. Absent leaves
  // the panel exactly as it was.
  const dnsProvider = status?.dnsProvider;
  // A connected domain reports its own choice. A domain connected before this
  // option existed reports nothing, and www is what it was set up as.
  const activeCanonical: CanonicalHost = hasDomain
    ? status?.canonical || "www"
    : canonical;

  return (
    <Panel>
      <Header>
        <Title>Custom domain</Title>
        {hasDomain && (
          <Tag variant={currentStatus === "active" ? "success" : "neutral"} size="sm" withDot>
            {StatusText[currentStatus] || "Not connected"}
          </Tag>
        )}
      </Header>
      <Intro>
        Serve this coach's website on their own domain. Enter it, add the DNS
        record we show at your registrar, and we verify and issue an SSL
        certificate automatically. We register both the root domain and the www
        version.
        {activeCanonical === "apex"
          ? " The root domain is the primary one. It needs an ALIAS or ANAME record, or CNAME flattening. The www version gets a plain CNAME and redirects to the root."
          : " The www version is the primary one. The root domain redirects to it."}
      </Intro>

      {!hasDomain && (
        <Row>
          <Field>
            <Label>Domain</Label>
            <Input
              placeholder="www.yourname.com"
              value={domain}
              onChange={(e: any) => setDomain(e.target.value)}
            />
          </Field>
          <Button variant="primary" onClick={onConnect} disabled={busy || !domain.trim() || !communityId}>
            {busy ? "Connecting..." : "Connect domain"}
          </Button>
        </Row>
      )}

      {!hasDomain && (
        <AdvancedToggle type="button" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? "Hide advanced" : "Advanced"}
        </AdvancedToggle>
      )}

      {!hasDomain && showAdvanced && (
        <Field>
          <Label>Primary address</Label>
          <Choice>
            <input
              type="radio"
              name="canonical-host"
              checked={canonical === "www"}
              onChange={() => setCanonical("www")}
            />
            <span>
              www.yourname.com <ChoiceHint>Default. Pick this unless the old site already ranked on the root.</ChoiceHint>
            </span>
          </Choice>
          <Choice>
            <input
              type="radio"
              name="canonical-host"
              checked={canonical === "apex"}
              onChange={() => setCanonical("apex")}
            />
            <span>
              yourname.com <ChoiceHint>The root domain. Your DNS must support ALIAS, ANAME or CNAME flattening.</ChoiceHint>
            </span>
          </Choice>
        </Field>
      )}

      {hasDomain && (
        <>
          <Row>
            <div style={{ fontFamily: "var(--font-sans)", fontSize: "var(--font-size-sm)" }}>
              {currentHost}
            </div>
            {currentStatus === "active" && (
              <a href={`https://${currentHost}`} target="_blank" rel="noreferrer" style={{ color: "var(--color-accent)", fontSize: "var(--font-size-sm)" }}>
                Open site
              </a>
            )}
          </Row>
          {dnsProvider && (
            <Provider>
              <span>Your DNS is managed by {dnsProvider.providerName}.</span>
              {dnsProvider.controlPanelUrl && (
                <ProviderLink
                  href={dnsProvider.controlPanelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open my DNS settings
                </ProviderLink>
              )}
            </Provider>
          )}
          {records.length > 0 && currentStatus !== "active" && (
            <Table>
              <HeadRow>
                <span>Type</span>
                <span>Name</span>
                <span>Value</span>
              </HeadRow>
              {records.map((r, i) => (
                <DnsRow key={i}>
                  <span>{r.type}</span>
                  <span>{r.name}</span>
                  <span>{r.value}</span>
                </DnsRow>
              ))}
            </Table>
          )}
          {status?.lastError && <Warn>{status.lastError} Check that no CAA record blocks Google Trust Services or Let's Encrypt.</Warn>}
          <Row>
            <Button variant="secondary" size="sm" onClick={refresh} disabled={busy}>
              {busy ? "Checking..." : "Re-check"}
            </Button>
            <Button variant="danger" size="sm" onClick={onDisconnect} disabled={busy}>
              Disconnect
            </Button>
          </Row>
        </>
      )}

      {error && <Warn>{error}</Warn>}
    </Panel>
  );
};

export default CustomDomainPanel;
